import { useState } from 'react'
import { Card, Button, Modal, Form, Row, Col, Spinner, Alert, Badge, InputGroup } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'
import AccountForm from './AccountForm'
import './ChartOfAccountsTree.css'

const ChartOfAccountsTree = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [expandedNodes, setExpandedNodes] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [filterActive, setFilterActive] = useState('all')

  const { data: clients } = useQuery(
    'clients-for-chart-of-accounts',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener árbol de cuentas (todas: generales + personalizadas de todos los clientes)
  const { data: treeData, isLoading, error } = useQuery(
    ['chart-of-accounts-tree', filterType, filterActive, searchTerm],
    async () => {
      const params = new URLSearchParams()
      if (filterType !== 'all') params.append('account_type', filterType)
      if (filterActive !== 'all') {
        params.append('is_active', filterActive === 'active' ? 'true' : 'false')
      }

      const response = await api.get(`/accounting/accounts/arbol/?${params.toString()}`)
      return response.data
    },
    { enabled: !!user }
  )

  // Mutación para crear/actualizar cuenta
  const accountMutation = useMutation(
    async (accountData) => {
      if (accountData.id) {
        return api.put(`/accounting/accounts/${accountData.id}/`, accountData)
      } else {
        return api.post('/accounting/accounts/', accountData)
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('chart-of-accounts-tree')
        setShowForm(false)
        setSelectedAccount(null)
        toast.success(selectedAccount ? 'Cuenta actualizada exitosamente' : 'Cuenta creada exitosamente')
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.message || 'Error al guardar cuenta'
        toast.error(message)
      }
    }
  )

  // Mutación para eliminar cuenta
  const deleteMutation = useMutation(
    async (accountId) => {
      // Primero verificar si se puede eliminar
      const canDelete = await api.get(`/accounting/accounts/${accountId}/puede_eliminar/`)
      if (!canDelete.data.puede_eliminar) {
        throw new Error('No se puede eliminar esta cuenta. Tiene transacciones o cuentas hijas.')
      }
      return api.delete(`/accounting/accounts/${accountId}/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('chart-of-accounts-tree')
        toast.success('Cuenta eliminada exitosamente')
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.message || 'Error al eliminar cuenta'
        toast.error(message)
      }
    }
  )

  const handleCreate = (parentId = null) => {
    setSelectedAccount({ parent: parentId })
    setShowForm(true)
  }

  const handleEdit = (account) => {
    setSelectedAccount(account)
    setShowForm(true)
  }

  const handleDelete = (account) => {
    if (window.confirm(`¿Estás seguro de eliminar la cuenta ${account.code} - ${account.name}?`)) {
      deleteMutation.mutate(account.id)
    }
  }

  const handleSubmit = (formData) => {
    accountMutation.mutate(formData)
  }

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes)
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId)
    } else {
      newExpanded.add(nodeId)
    }
    setExpandedNodes(newExpanded)
  }

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0
    const isExpanded = expandedNodes.has(node.id)
    const indent = level * 20

    return (
      <div key={node.id} className="tree-node">
        <div 
          className="tree-node-content"
          style={{ paddingLeft: `${indent}px` }}
        >
          <div className="d-flex align-items-center">
            <span className="tree-toggle-wrapper d-inline-flex align-items-center justify-content-center" style={{ width: 24, minWidth: 24, flexShrink: 0 }}>
              {hasChildren ? (
                <Button
                  variant="link"
                  size="sm"
                  className="tree-toggle p-0 m-0"
                  onClick={() => toggleNode(node.id)}
                >
                  {isExpanded ? '▼' : '▶'}
                </Button>
              ) : null}
            </span>
            <Badge bg={getAccountTypeBadge(node.account_type)} className="me-2">
              {node.code}
            </Badge>
            <span className="me-2">{node.name}</span>
            {node.client_id && (
              <Badge bg="secondary" className="ms-1">
                {(() => {
                  const c = clients?.find(x => x.id == node.client_id)
                  const prefijo = (c?.clave_prefijo || '').trim()
                  return prefijo ? `Cliente - ${prefijo}` : 'Cliente'
                })()}
              </Badge>
            )}
            {!node.is_active && <Badge bg="secondary">Inactiva</Badge>}
            {node.is_deductible && <Badge bg="info" className="ms-2">Deducible</Badge>}
            <div className="ms-auto">
              <Button
                variant="outline-primary"
                size="sm"
                className="me-1"
                onClick={() => handleCreate(node.id)}
              >
                + Hijo
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-1"
                onClick={() => handleEdit(node)}
              >
                Editar
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDelete(node)}
                disabled={deleteMutation.isLoading}
              >
                Eliminar
              </Button>
            </div>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="tree-children">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  const getAccountTypeBadge = (type) => {
    const badges = {
      'ACTIVO': 'primary',
      'PASIVO': 'danger',
      'CAPITAL': 'success',
      'INGRESO': 'info',
      'GASTO': 'warning',
      'COSTO': 'secondary',
      'RESULTADO_FINANCIAMIENTO': 'info',
      'CUENTAS_ORDEN': 'dark'
    }
    return badges[type] || 'secondary'
  }

  const filterTree = (nodes, searchTerm) => {
    if (!searchTerm || !nodes) return nodes
    
    const searchLower = searchTerm.toLowerCase()
    
    return nodes.map(node => {
      const matches = (node.code && node.code.toLowerCase().includes(searchLower)) ||
                     (node.name && node.name.toLowerCase().includes(searchLower))
      
      // Filtrar hijos recursivamente
      const filteredChildren = node.children && node.children.length > 0 
        ? filterTree(node.children, searchTerm) 
        : []
      
      // Si el nodo coincide o tiene hijos que coinciden, incluirlo
      if (matches || filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren
        }
      }
      return null
    }).filter(node => node !== null)
  }

  const filteredTree = treeData && searchTerm ? filterTree(treeData, searchTerm) : treeData

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Plan de Cuentas</h3>
        <div>
          <Button variant="outline-primary" className="me-2" onClick={() => handleCreate()}>
            + Nueva Cuenta Raíz
          </Button>
          <Button variant="outline-secondary" onClick={() => {
            // Importar desde Excel (funcionalidad futura)
            toast.info('Funcionalidad de importación en desarrollo')
          }}>
            Importar desde Excel
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>🔍</InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Buscar por código o nombre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">Todos los tipos</option>
                <option value="ACTIVO">Activo</option>
                <option value="PASIVO">Pasivo</option>
                <option value="CAPITAL">Capital</option>
                <option value="INGRESO">Ingreso</option>
                <option value="GASTO">Gasto</option>
                <option value="COSTO">Costo</option>
                <option value="RESULTADO_FINANCIAMIENTO">Resultado financiamiento</option>
                <option value="CUENTAS_ORDEN">Cuentas de orden</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
              >
                <option value="all">Todas</option>
                <option value="active">Activas</option>
                <option value="inactive">Inactivas</option>
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex justify-content-end gap-2">
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => {
                  // Expandir todo
                  const allIds = []
                  const collectIds = (nodes) => {
                    nodes.forEach(node => {
                      if (node.children && node.children.length > 0) {
                        allIds.push(node.id)
                        collectIds(node.children)
                      }
                    })
                  }
                  if (filteredTree) collectIds(filteredTree)
                  setExpandedNodes(new Set(allIds))
                }}
              >
                Expandir Todo
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  // Contraer todo
                  setExpandedNodes(new Set())
                }}
              >
                Contraer Todo
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Árbol de cuentas */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">
          Error al cargar plan de cuentas. Por favor, intenta nuevamente.
        </Alert>
      ) : filteredTree && filteredTree.length > 0 ? (
        <Card>
          <Card.Body>
            <div className="chart-tree">
              {filteredTree.map(node => renderTreeNode(node))}
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-muted">No se encontraron cuentas</p>
            <Button variant="primary" onClick={() => handleCreate()}>
              Crear primera cuenta
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Modal de formulario */}
      <Modal show={showForm} onHide={() => { setShowForm(false); setSelectedAccount(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedAccount?.id ? 'Editar Cuenta' : 'Nueva Cuenta'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AccountForm
            account={selectedAccount}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); setSelectedAccount(null) }}
            isLoading={accountMutation.isLoading}
          />
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default ChartOfAccountsTree

